'use client';

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Player as PlayController, cn } from '@crabs-ai/ds-react';
import { Close, PauseCircleFill, PlayCircleFill } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { convertMillisecondsToTimeFormat } from '@/shared';
import { useIdleTimer } from '@/shared/hooks/useIdleTimer';
import { useMediaPlayer } from '@/shared/hooks/useMediaPlayer';
import useFullScreen from '@/shared/hooks/useFullScreen';
import { CHANNEL_TALK_SYNC_BUTTON_VISIBILITY_EVENT } from '../channel-talk/channel-talk-events';
import { VideoContent } from './adapters/VideoContent';
import { AudioContent } from './adapters/AudioContent';
import { ImageContent } from './adapters/ImageContent';
import type { PreviewMediaType } from './types';

const IDLE_TIMEOUT = 1200;

// rendering-hoist-jsx: 정적 요소 호이스팅
const closeIconClassName = 'size-[24px] [&_path]:fill-sys-cont-neutral-default';
const playIconClassName = 'size-[48px] [&_path]:fill-sys-cont-inverse-light';

interface MediaPreviewModalProps {
  /** 모달 열림 여부 */
  isOpen: boolean;
  /** 헤더 제목 */
  title: string;
  /** 미디어 타입 */
  mediaType: PreviewMediaType;
  /** 미디어 URL */
  mediaUrl: string;
  /** 닫기 콜백 */
  onClose: () => void;
  /** 씬 구간 재생 — 비디오 전용 (없으면 전체 재생) */
  sceneRange?: { startSeconds: number; endSeconds: number };
  /** 서버에서 받은 duration (초). 미디어 메타데이터 로드 전 fallback */
  fallbackDuration?: number;
  /** 헤더 우측 추가 요소 (다운로드 버튼 등) */
  headerExtra?: React.ReactNode;
  /** 이전 영상 이동 */
  onSkipPrevious?: () => void;
  /** 다음 영상 이동 */
  onSkipNext?: () => void;
  isSkipPreviousDisabled?: boolean;
  isSkipNextDisabled?: boolean;
  /** 전체화면 버튼 표시 여부 (기본 false) */
  showFullscreen?: boolean;
}

/**
 * 통합 미디어 프리뷰 모달 — 외부 게이트
 * isOpen=false일 때 내부 컴포넌트를 마운트하지 않아
 * 훅의 effect가 미디어 엘리먼트 없이 실행되는 문제를 방지한다.
 *
 * 주의: 이 컴포넌트에는 훅을 추가하지 말 것 (조건부 return 사용)
 */
export const MediaPreviewModal = memo(function MediaPreviewModal(
  props: MediaPreviewModalProps,
) {
  if (!props.isOpen) return null;
  if (typeof document === 'undefined') return null;

  // mediaUrl을 key로 사용하여 영상 변경 시 컴포넌트 리마운트 → 상태 초기화
  return createPortal(
    <MediaPreviewModalInner
      key={props.mediaUrl}
      {...props}
    />,
    document.body,
  );
});

/**
 * 내부 모달 콘텐츠 — isOpen=true일 때만 마운트
 * 훅이 마운트 시점에 실행되므로 mediaRef.current가 유효하다.
 */
const MediaPreviewModalInner = memo(function MediaPreviewModalInner({
  title,
  mediaType,
  mediaUrl,
  onClose,
  sceneRange,
  fallbackDuration,
  headerExtra,
  onSkipPrevious,
  onSkipNext,
  isSkipPreviousDisabled = true,
  isSkipNextDisabled = true,
  showFullscreen = false,
}: Omit<MediaPreviewModalProps, 'isOpen'>) {
  const t = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);

  // C-3: body 스크롤 잠금
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // 모달 열림 시 채널톡 버튼 숨김. 닫힐 때는 중앙 정책에 재동기화만 요청한다.
  useEffect(() => {
    window.ChannelIO?.('hideChannelButton');
    return () => {
      window.dispatchEvent(
        new Event(CHANNEL_TALK_SYNC_BUTTON_VISIBILITY_EVENT),
      );
    };
  }, []);

  const isPlayable = mediaType === 'video' || mediaType === 'audio';
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  const handleMediaLoaded = useCallback(() => {
    setIsMediaLoaded(true);
  }, []);

  const { isIdle, resetIdleTimer, forceIdle } = useIdleTimer(IDLE_TIMEOUT);

  const {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    seekStepInSeconds,
    handlePlayPause,
    handleTimeChange,
    goToPreviousSeconds,
    goToNextSeconds,
    changeVolume,
    handleSoundToggle,
  } = useMediaPlayer({ mediaRef, sceneRange, fallbackDuration });

  // W-2: 외부 ref 전달 + 키보드는 이 컴포넌트에서 통합 관리
  const { isFullscreen, toggleFullscreen } = useFullScreen({
    isFullscreen: showFullscreen,
    ref: containerRef,
    enableKeyboard: false, // 키보드는 아래 통합 핸들러에서 처리
  });

  // 볼륨 조절 콜백
  const handleVolumeDown = useCallback(() => {
    changeVolume(Math.max(0, volume - 5));
  }, [volume, changeVolume]);

  const handleVolumeUp = useCallback(() => {
    changeVolume(Math.min(100, volume + 5));
  }, [volume, changeVolume]);

  // idle 타이머 연동 재생/일시정지 (로딩 완료 전 차단)
  const handlePlayPauseWithIdle = useCallback(() => {
    if (!isMediaLoaded) return;
    handlePlayPause();
    resetIdleTimer();
  }, [isMediaLoaded, handlePlayPause, resetIdleTimer]);

  // 키보드 단축키 (통합 핸들러)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement;
      // input/textarea에서는 단축키 무시
      if (active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case ' ':
          // C-2: 버튼/링크에 포커스된 상태면 기본 동작(클릭) 허용
          if (active?.tagName === 'BUTTON' || active?.tagName === 'A') return;
          e.preventDefault();
          if (isPlayable && isMediaLoaded) handlePlayPauseWithIdle();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (isPlayable && isMediaLoaded) goToPreviousSeconds();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (isPlayable && isMediaLoaded) goToNextSeconds();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (isPlayable && isMediaLoaded) handleVolumeUp();
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (isPlayable && isMediaLoaded) handleVolumeDown();
          break;
        case 'm':
        case 'M':
          if (isPlayable && isMediaLoaded) handleSoundToggle();
          break;
        case 'f':
        case 'F':
          if (showFullscreen) toggleFullscreen();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isPlayable,
    isMediaLoaded,
    onClose,
    handlePlayPauseWithIdle,
    goToPreviousSeconds,
    goToNextSeconds,
    handleVolumeUp,
    handleVolumeDown,
    handleSoundToggle,
    showFullscreen,
    toggleFullscreen,
  ]);

  const timeInSeconds = useMemo(
    () => ({ start: 0, end: duration }),
    [duration],
  );

  // 콘텐츠 어댑터 렌더링
  const renderContent = () => {
    switch (mediaType) {
      case 'video':
        return (
          <VideoContent
            url={mediaUrl}
            name={title}
            mediaRef={mediaRef}
            onLoaded={handleMediaLoaded}
          />
        );
      case 'audio':
        return (
          <AudioContent
            url={mediaUrl}
            name={title}
            mediaRef={mediaRef}
            onLoaded={handleMediaLoaded}
          />
        );
      case 'image':
        return (
          <ImageContent
            url={mediaUrl}
            name={title}
            onLoaded={handleMediaLoaded}
          />
        );
    }
  };

  return (
    // W-1: 접근성 — role, aria-modal, aria-label
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[9999] flex flex-col overflow-hidden bg-sys-overlay-dim"
      onClick={onClose}
    >
      {/* 헤더 */}
      <div
        className="flex h-[64px] w-full items-center justify-between border-b-1 border-sys-style-neutral-default bg-sys-bg-surface layout-padding-side py-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex min-w-0 flex-1 items-center gap-sm overflow-hidden pr-sm">
          {/* W-1: 닫기 아이콘을 button으로 감싸기 */}
          <button
            type="button"
            aria-label={t('Player.close') ?? '닫기'}
            onClick={onClose}
            className="shrink-0"
          >
            <Close className={closeIconClassName} />
          </button>
          <span className="block min-w-0 flex-1 overflow-hidden text-title-xlarge text-ellipsis whitespace-nowrap text-sys-cont-neutral-default">
            {title}
          </span>
        </div>
        {headerExtra ? (
          <div className="flex shrink-0 items-center gap-sm">{headerExtra}</div>
        ) : null}
      </div>

      {/* 콘텐츠 영역 */}
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center bg-sys-overlay-dim"
        onClick={(e) => e.stopPropagation()}
        onMouseMove={isPlayable ? resetIdleTimer : undefined}
        onMouseLeave={isPlayable ? forceIdle : undefined}
      >
        {renderContent()}

        {/* 센터 오버레이 — 비디오만 (로딩 완료 후, idle 시 숨김) */}
        {mediaType === 'video' && isMediaLoaded ? (
          <AnimatePresence>
            {!isIdle ? (
              <motion.button
                type="button"
                aria-label={isPlaying ? t('Player.pause') : t('Player.play')}
                className="absolute inset-0 flex cursor-pointer items-center justify-center bg-transparent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPauseWithIdle();
                }}
              >
                {isPlaying ? (
                  <PauseCircleFill className={playIconClassName} />
                ) : (
                  <PlayCircleFill className={playIconClassName} />
                )}
              </motion.button>
            ) : null}
          </AnimatePresence>
        ) : null}
      </div>

      {/* 컨트롤러 — playable 미디어만 */}
      {isPlayable ? (
        <div onClick={(e) => e.stopPropagation()}>
          <PlayController
            className="bg-sys-bg-surface py-sm"
            audioProps={{
              className: cn('max-w-[calc(160px+36px)]'),
            }}
            isPlaying={isPlaying}
            isShowFullscreen={showFullscreen}
            isShowRepeat={false}
            time={timeInSeconds}
            currentTime={currentTime}
            currentTimeText={convertMillisecondsToTimeFormat(
              currentTime * 1000,
            )}
            endTimeText={convertMillisecondsToTimeFormat(duration * 1000)}
            onFastForward={isMediaLoaded ? goToNextSeconds : undefined}
            onFastRewind={isMediaLoaded ? goToPreviousSeconds : undefined}
            onFullscreen={showFullscreen ? toggleFullscreen : undefined}
            onPlay={handlePlayPauseWithIdle}
            onPause={handlePlayPauseWithIdle}
            onTimeChange={isMediaLoaded ? handleTimeChange : undefined}
            step={0.1}
            volume={isMuted ? 0 : volume}
            onVolumeChange={changeVolume}
            onToggleVolume={handleSoundToggle}
            volumeTooltip={{
              node:
                isMuted || volume === 0 ? t('Player.unmute') : t('Player.mute'),
              subNode: 'm',
            }}
            playAndPauseTooltip={{
              node: isPlaying ? t('Player.pause') : t('Player.play'),
              subNode: 'Space',
            }}
            isSkipPreviousDisabled={isSkipPreviousDisabled}
            isSkipNextDisabled={isSkipNextDisabled}
            onSkipPrevious={onSkipPrevious}
            onSkipNext={onSkipNext}
            skipPreviousTooltip={{
              node: t('Player.previous-video'),
            }}
            skipNextTooltip={{
              node: t('Player.next-video'),
            }}
            fastRewindTooltip={{
              node: t('Player.previous-seconds', {
                seconds: seekStepInSeconds,
              }),
              subNode: '←',
            }}
            fastForwardTooltip={{
              node: t('Player.next-seconds', {
                seconds: seekStepInSeconds,
              }),
              subNode: '→',
            }}
            {...(showFullscreen
              ? {
                  fullscreenTooltip: {
                    node: isFullscreen
                      ? t('Player.full-screen-exit')
                      : t('Player.full-screen'),
                    subNode: 'f',
                  },
                }
              : {})}
            portalProps={{
              container: containerRef.current,
            }}
          />
        </div>
      ) : null}
    </div>
  );
});
